import React from 'react'
import { Card, Row, Col, Spinner, Badge } from 'react-bootstrap'
import { NetworkInfo } from '../types'
import * as S from '../styles'

interface NetworkInfoCardProps {
  data?: NetworkInfo
  loading: boolean
}

export const NetworkInfoCard: React.FC<NetworkInfoCardProps> = ({
  data,
  loading
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  if (loading || !data) {
    return (
      <Card className="h-100">
        <Card.Header>
          <Card.Title as="h5">
            <i className="feather icon-shield text-success mr-2" />
            Informações da Blockchain
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <S.LoadingSpinner>
            <Spinner animation="border" />
          </S.LoadingSpinner>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card className="h-100">
      <Card.Header>
        <Card.Title as="h5">
          <i className="feather icon-shield text-success mr-2" />
          Informações da Blockchain
        </Card.Title>
        <div className="card-header-right">
          <Badge variant="success" className="ml-2">
            <i className="feather icon-check-circle mr-1" />
            Rede Ativa
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="mb-4 text-center">
          <div className="bg-light p-3 rounded mb-3">
            <h4 className="text-primary mb-2">{data.networkName}</h4>
            <p className="text-muted mb-0">{data.networkType}</p>
          </div>
        </div>

        <Row className="mb-4">
          <Col sm={6}>
            <div className="text-center mb-3">
              <div className="bg-light border border-primary p-3 rounded">
                <i
                  className="feather icon-cpu text-primary mb-2"
                  style={{ fontSize: '2rem' }}
                />
                <h5 className="text-dark mb-1">{data.consensusAlgorithm}</h5>
                <p className="text-muted mb-0 small">Algoritmo de Consenso</p>
              </div>
            </div>
          </Col>
          <Col sm={6}>
            <div className="text-center mb-3">
              <div className="bg-light border border-success p-3 rounded">
                <i
                  className="feather icon-clock text-success mb-2"
                  style={{ fontSize: '2rem' }}
                />
                <h5 className="text-dark mb-1">{data.blockTime}</h5>
                <p className="text-muted mb-0 small">Tempo de Bloco</p>
              </div>
            </div>
          </Col>
        </Row>

        <div className="border-top pt-3 mb-4">
          <h6 className="mb-3">Especificações da Rede:</h6>
          <div className="row">
            <div className="col-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Total de Nós:</span>
                <Badge variant="info">{data.totalNodes}</Badge>
              </div>
            </div>
            <div className="col-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Validadores Ativos:</span>
                <Badge variant="success">{data.activeValidators}</Badge>
              </div>
            </div>
            <div className="col-12 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Tempo de Atividade:</span>
                <Badge variant="primary">{data.networkUptime}</Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="border-top pt-3 mb-4">
          <h6 className="mb-3">Status da Blockchain:</h6>
          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Último Bloco:</span>
              <code className="bg-light px-2 py-1 rounded">
                #{formatNumber(data.lastBlockHeight)}
              </code>
            </div>
          </div>
          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Timestamp:</span>
              <small className="text-muted">
                {formatDate(data.lastBlockTimestamp)}
              </small>
            </div>
          </div>
        </div>

        <div className="border-top pt-3 mb-4">
          <h6 className="mb-3">Garantias de Segurança:</h6>

          <div className="mb-3">
            <div className="d-flex align-items-start">
              <i className="feather icon-shield text-success mr-2 mt-1" />
              <div>
                <strong className="text-success">Segurança da Rede</strong>
                <p className="text-muted mb-0 small">{data.networkSecurity}</p>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="d-flex align-items-start">
              <i className="feather icon-lock text-info mr-2 mt-1" />
              <div>
                <strong className="text-info">Imutabilidade dos Dados</strong>
                <p className="text-muted mb-0 small">{data.dataImmutability}</p>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="d-flex align-items-start">
              <i className="feather icon-eye text-warning mr-2 mt-1" />
              <div>
                <strong className="text-warning">Transparência</strong>
                <p className="text-muted mb-0 small">
                  {data.transparencyLevel}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-top pt-3">
          <div className="text-center">
            <div className="bg-light border border-success p-3 rounded">
              <i
                className="feather icon-award text-success mb-2"
                style={{ fontSize: '1.5rem' }}
              />
              <h6 className="text-dark mb-1">Certificação Enterprise</h6>
              <p className="text-muted mb-0 small">
                Blockchain privada certificada para aplicações empresariais
              </p>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
